/**
 * What a certification owner can reach without a platform subscription.
 *
 * The certification and the platform are two purchases. A coach who bought
 * only the cert gets the cert, and that is deliberate. What was not
 * deliberate is what it felt like from the outside:
 *
 *   "I recently purchased the coaching certification and when I try to access
 *    it via the platform, it says I need the subscription for the coaching
 *    platform. I can access the certification via the specific link in the
 *    purchase email but then everything is locked [...] I can't even access
 *    my settings and profile without it saying I need the subscription."
 *
 * Both halves of that were real. The web paywall had no link to the
 * certification at all, so the only door to a thing they owned was the link
 * in the receipt. And account settings sat behind the paywall, so they could
 * not change their password or delete their account.
 *
 * The second one is also an App Store problem rather than only a rude one:
 * Apple §5.1.1(v) requires an account created in the app to be deletable in
 * the app, and `settings` is where `delete-account` lives. On native the
 * old code redirected a cert holder to #/certified on every tap, so there
 * was no path to it whatsoever.
 *
 * This file is the rule, kept separate from BAMFull because BAMFull pulls
 * supabaseClient and could not then be imported by a test.
 *
 * No em dashes.
 */

/** The certification itself: what they actually bought. */
export const isCertRoute = (nav) =>
  !!nav && (nav === "certified" || nav.startsWith("certified/"));

/** The cert purchase flow, which has to stay open so they can buy it. */
export const isBuyCertRoute = (nav) =>
  !!nav && (nav === "buy-cert" || nav.startsWith("buy-cert"));

/**
 * Account management. Not a perk: this is where a coach changes their email,
 * manages billing and deletes their account, and none of that should require
 * a subscription they decided not to buy.
 */
export const isAccountRoute = (nav) => nav === "settings";

/**
 * Every route a cert owner reaches with no active platform subscription.
 *
 * Note what is NOT here: the dashboard, the drills, the community. Those are
 * the subscription, and a cert owner still sees the paywall for them. The
 * complaint was never that the paywall exists, it was that there was no way
 * past it to the thing they had paid for.
 */
export function certOwnerMayView(nav) {
  return isCertRoute(nav) || isBuyCertRoute(nav) || isAccountRoute(nav);
}

/**
 * The six months of platform that come with the certification.
 *
 * The cert page promises it in as many words: "The first six months of the
 * platform are complimentary while you work through it." The webhook honours
 * that, writing `cert_active` and `cert_pause_resumes_at` (182 days out) onto
 * the profile at purchase.
 *
 * Nothing read either column. The gate resolved membership from
 * `stripe_subscription_active || cert_founding` and nothing else, so the
 * window was granted in the database and ignored everywhere it mattered.
 * Every post-launch cert buyer was paywalled out of six months they had paid
 * for, which is what Jonathon Pedro wrote in about on the first day it could
 * possibly have happened to anyone.
 *
 * Dates here are compared as instants, and a missing or unparseable date is
 * treated as no window rather than an open one: failing closed on a date we
 * cannot read is wrong in a recoverable way, failing open is not.
 */
export function certComplimentaryActive({ certPurchased, pauseResumesAt, now = Date.now() }) {
  if (!certPurchased || !pauseResumesAt) return false;
  const ends = Date.parse(pauseResumesAt);
  if (Number.isNaN(ends)) return false;
  return ends > now;
}

/**
 * The gate itself.
 *
 * `isSubscribed` is deliberately compared against `false` rather than
 * falsy: it is null while the membership check is still in flight, and
 * treating "not yet known" as "not subscribed" would flash the paywall at a
 * paying coach on every slow boot.
 */
export function needsPaywall({ signedIn, isSubscribed, hasCert, nav, hasAfs = false, afsCoach = hasAfs, hasFilmDesk = false }) {
  if (!signedIn) return false;
  if (isSubscribed !== false) return false;
  if (isBuyCertRoute(nav)) return false;
  if (hasCert && certOwnerMayView(nav)) return false;
  // Advantage Flow bought on its own (2026-10-24): its page, and account settings, without a platform membership.
  if (hasAfs && afsOwnerMayView(nav, afsCoach)) return false;
  // Film Desk contributors (2026-11-02) help find clips on a free account: the Film Desk, and account settings.
  if (hasFilmDesk && filmDeskMayView(nav)) return false;
  return true;
}

/** Where a Film Desk contributor without a membership may go: the Film Desk and their own settings. */
export function filmDeskMayView(nav = "") {
  const n = String(nav || "");
  return n === "filmdesk" || n.startsWith("filmdesk/") || n === "settings";
}

/**
 * Where someone with Advantage Flow but no platform membership may go: AFS itself and their own settings. A coach
 * (not a tagger) also gets player development plans and practice planning (Coleman, 2026-10-28: "they only get access
 * to PDPs and practice planning").
 */
const AFS_COACH_ROUTES = ["dev-studio", "plans"];
export function afsOwnerMayView(nav = "", coach = false) {
  const n = String(nav || "");
  if (n === "afs" || n.startsWith("afs/") || n === "settings") return true;
  return coach && AFS_COACH_ROUTES.some(r => n === r || n.startsWith(`${r}/`));
}

/**
 * Where to send a cert owner who has landed somewhere they cannot see.
 *
 * Only used on native, where there is no Stripe paywall to render (Apple
 * forbids it), so the choice is a redirect or a dead end. Returns null when
 * they are already somewhere legitimate, which is what stops the old
 * behaviour of bouncing them to #/certified on every single tap and thereby
 * sealing off their own account settings.
 */
export function certOwnerRedirect(nav) {
  return certOwnerMayView(nav) ? null : "#/certified";
}

/* An Apple (iOS in-app) subscription as the server last saw it: the
   RevenueCat webhook keeps iap_subscription_active and iap_expires_at on the
   profile. The app used to ignore both and wait on a live RevenueCat answer,
   so an iOS subscriber saw the paywall until (and whenever) that answer was
   slow, and always on the web (Coleman, 2026-10-15: iOS subscribers get the
   web platform too). Active means flagged and not past its expiry; a missing
   expiry trusts the flag. */
export function iapStoredActive({ active, expiresAt, now = Date.now() }) {
  if (!active) return false;
  if (!expiresAt) return true;
  const t = new Date(expiresAt).getTime();
  return Number.isNaN(t) ? true : t > now;
}
